<template>
    <section class="mobile-question-add">
        <van-nav-bar
            title="我要提问"
            left-arrow
            fixed
            placeholder
            z-index="100"
            safe-area-inset-top
            @click-left="goBack"
        />

        <main class="mobile-question-add__content">
            <section class="mobile-question-add__intro">
                <span class="mobile-question-add__badge">BIGEMAP 问答</span>
                <h1>描述你遇到的问题</h1>
                <p>补充操作过程和报错现象，清晰的问题更容易获得准确回答。</p>
            </section>

            <section class="mobile-question-add__card">
                <div class="mobile-question-add__field-title">
                    <strong>问题标题</strong>
                    <span>{{ answer.title.length }}/100</span>
                </div>
                <van-field
                    v-model.trim="answer.title"
                    type="textarea"
                    rows="2"
                    autosize
                    maxlength="100"
                    placeholder="请用一句话说明问题（5～100 字）"
                    class="mobile-question-add__title-input"
                    @blur="validateTitle(false)"
                />
                <p v-if="errors.title" class="mobile-question-add__error">{{ errors.title }}</p>
            </section>

            <section v-if="isAdmin" class="mobile-question-add__card">
                <div class="mobile-question-add__field-title">
                    <strong>问题分类</strong>
                    <span>管理员必选</span>
                </div>
                <div class="mobile-question-add__categories" role="radiogroup" aria-label="问题分类">
                    <button
                        v-for="item in answerTypes"
                        :key="item.value"
                        type="button"
                        role="radio"
                        :aria-checked="sameType(answer.type_id, item.value)"
                        :class="{'mobile-question-add__category--active': sameType(answer.type_id, item.value)}"
                        class="mobile-question-add__category"
                        @click="selectType(item.value)"
                    >
                        <span></span>
                        {{ item.label }}
                    </button>
                </div>
                <p v-if="errors.type" class="mobile-question-add__error">{{ errors.type }}</p>
            </section>

            <section class="mobile-question-add__card mobile-question-add__editor-card">
                <div class="mobile-question-add__field-title">
                    <strong>问题详情</strong>
                    <span>支持插入图片</span>
                </div>
                <div class="mobile-question-add__editor-shell" :class="{'mobile-question-add__editor-shell--error': errors.content}">
                    <Toolbar
                        class="mobile-question-add__toolbar"
                        :editor="editor"
                        :default-config="toolbarConfig"
                    />
                    <Editor
                        v-model="answer.content"
                        class="mobile-question-add__editor"
                        :default-config="editorConfig"
                        @onCreated="editorCreated"
                        @onBlur="validateContent(false)"
                    />
                </div>
                <p v-if="errors.content" class="mobile-question-add__error">{{ errors.content }}</p>
                <div class="mobile-question-add__tips">
                    <span>建议包含</span>
                    <em>使用的产品</em>
                    <em>操作步骤</em>
                    <em>报错截图</em>
                </div>
            </section>
        </main>

        <footer class="mobile-question-add__footer">
            <van-button
                type="info"
                block
                round
                :loading="submitting"
                loading-text="正在发布…"
                @click="submit"
            >
                发布问题
            </van-button>
        </footer>

        <input
            ref="imageInput"
            class="mobile-question-add__image-input"
            type="file"
            accept="image/*"
            multiple
            tabindex="-1"
            aria-hidden="true"
            @change="handleImageSelected"
        >
    </section>
</template>

<script>
import '@wangeditor/editor/dist/css/style.css';
import {Editor, Toolbar} from '@wangeditor/editor-for-vue';

export default {
    name: 'MobileQuestionAdd',
    components: {Editor, Toolbar},
    data() {
        return {
            editor: null,
            pendingImageInsert: null,
            submitting: false,
            answer: {
                title: this.$route.query.title || '',
                type_id: 0,
                content: '',
                attach_list: [],
            },
            errors: {
                title: '',
                type: '',
                content: '',
            },
            toolbarConfig: {
                toolbarKeys: [
                    'bold',
                    'underline',
                    'bulletedList',
                    'numberedList',
                    'insertLink',
                    'uploadImage',
                    '|',
                    'undo',
                    'redo',
                ],
            },
            editorConfig: {
                autoFocus: false,
                placeholder: '请详细说明操作过程、出现的现象以及你希望达到的效果…',
                MENU_CONF: {
                    uploadImage: {
                        customBrowseAndUpload: (insertFn) => {
                            const input = this.$refs.imageInput;
                            if (!input) {
                                return;
                            }
                            this.pendingImageInsert = insertFn;
                            input.value = '';
                            input.click();
                        },
                        customUpload: (file, insertFn) => this.uploadImage(file, insertFn),
                    },
                },
            },
        };
    },
    computed: {
        isAdmin() {
            return !!this.$store.state.userData && !!this.$store.state.userData.isAdmin;
        },
        answerTypes() {
            return this.$store.state.dict && this.$store.state.dict.answer_type || [];
        },
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        editorCreated(editor) {
            this.editor = Object.seal(editor);
        },
        sameType(current, value) {
            return String(current) === String(value);
        },
        selectType(value) {
            this.answer.type_id = value;
            this.errors.type = '';
        },
        async handleImageSelected(event) {
            const files = Array.from(event.target.files || []);
            const insertFn = this.pendingImageInsert;
            this.pendingImageInsert = null;
            event.target.value = '';
            if (!insertFn || files.length === 0) {
                return;
            }
            for (const file of files) {
                await this.uploadImage(file, insertFn);
            }
        },
        async uploadImage(file, insertFn) {
            const formData = new FormData();
            formData.append('image', file);
            try {
                const response = await this.$axiosUpload.post('', formData);
                const src = response && response.data && response.data.src;
                if (!src) {
                    throw new Error('missing image url');
                }
                insertFn(src);
            } catch (error) {
                this.$toast.fail(`${file.name || '图片'}上传失败，请重试`);
            }
        },
        validateTitle(showToast) {
            const title = this.answer.title.trim();
            if (!title) {
                this.errors.title = '请输入问题标题';
            } else if (title.length < 5) {
                this.errors.title = '问题标题至少需要 5 个字';
            } else {
                this.errors.title = '';
            }
            if (showToast && this.errors.title) {
                this.$toast(this.errors.title);
            }
            return !this.errors.title;
        },
        validateContent(showToast) {
            const text = this.editor ? this.editor.getText().replace(/\s/g, '') : '';
            if (text.length < 5) {
                this.errors.content = '请再补充一些问题详情';
            } else {
                this.errors.content = '';
            }
            if (showToast && this.errors.content) {
                this.$toast(this.errors.content);
            }
            return !this.errors.content;
        },
        validateType() {
            if (this.isAdmin && !this.answer.type_id) {
                this.errors.type = '请选择问题分类';
            } else {
                this.errors.type = '';
            }
            return !this.errors.type;
        },
        submit() {
            const titleValid = this.validateTitle(false);
            const contentValid = this.validateContent(false);
            const typeValid = this.validateType();
            if (!titleValid || !contentValid || !typeValid || this.submitting) {
                this.$toast(this.errors.title || this.errors.content || this.errors.type);
                return;
            }
            this.submitting = true;
            this.$bbs.post('/bbs/answer/index', this.answer, {
                need: {error: true, loading: false},
            }).then((response) => {
                this.$toast.success('发布成功');
                this.$router.replace({path: `/wenda/detail/${response.data.answer_id}.html`});
            }).catch(() => {
                this.$toast.fail('发布失败，请稍后重试');
            }).finally(() => {
                this.submitting = false;
            });
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.push({path: '/index'});
        },
    },
};
</script>

<style scoped lang="less">
.mobile-question-add {
    min-height: 100vh;
    padding-bottom: calc(88px + env(safe-area-inset-bottom));
    color: #1e293b;
    background: #f4f6f9;
}

.mobile-question-add__content {
    padding: 14px 12px 24px;
}

.mobile-question-add__intro {
    padding: 18px 18px 20px;
    margin-bottom: 12px;
    color: #fff;
    background: linear-gradient(135deg, #226eea, #4197ff);
    border-radius: 14px;
    box-shadow: 0 10px 28px rgba(36, 120, 242, 0.2);

    h1 {
        margin: 9px 0 6px;
        font-size: 21px;
        font-weight: 600;
        line-height: 1.35;
    }

    p {
        margin: 0;
        color: rgba(255, 255, 255, 0.82);
        font-size: 13px;
        line-height: 20px;
    }
}

.mobile-question-add__badge {
    display: inline-flex;
    padding: 4px 8px;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.4px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 12px;
}

.mobile-question-add__card {
    padding: 16px;
    margin-bottom: 12px;
    background: #fff;
    border: 1px solid #e9edf3;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(31, 45, 61, 0.04);
}

.mobile-question-add__field-title {
    display: flex;
    margin-bottom: 12px;
    align-items: center;
    justify-content: space-between;

    strong {
        font-size: 16px;
        font-weight: 600;
    }

    span {
        color: #9aa4b2;
        font-size: 12px;
    }
}

.mobile-question-add__title-input {
    padding: 12px;
    background: #f7f9fc;
    border: 1px solid #e5eaf1;
    border-radius: 8px;

    /deep/ .van-field__control {
        min-height: 48px;
        color: #253047;
        font-size: 15px;
        line-height: 23px;
    }
}

.mobile-question-add__categories {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
}

.mobile-question-add__category {
    display: flex;
    min-height: 44px;
    padding: 9px 11px;
    align-items: center;
    gap: 8px;
    color: #5f6c80;
    font-size: 13px;
    line-height: 18px;
    text-align: left;
    background: #f7f9fc;
    border: 1px solid #e3e8f0;
    border-radius: 8px;

    span {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
        box-sizing: border-box;
        background: #fff;
        border: 1.5px solid #c4ccd8;
        border-radius: 50%;
    }
}

.mobile-question-add__category--active {
    color: #2478f2;
    font-weight: 500;
    background: #edf4ff;
    border-color: #9fc1f7;

    span {
        border: 4px solid #2478f2;
    }
}

.mobile-question-add__editor-card {
    padding-right: 12px;
    padding-left: 12px;
}

.mobile-question-add__editor-shell {
    overflow: hidden;
    border: 1px solid #dfe5ed;
    border-radius: 9px;
    transition: border-color 0.2s;
}

.mobile-question-add__editor-shell--error {
    border-color: #ee6a67;
}

.mobile-question-add__toolbar {
    overflow-x: auto;
    background: #f8fafc;
    border-bottom: 1px solid #e5eaf1;
    -webkit-overflow-scrolling: touch;

    /deep/ .w-e-toolbar {
        min-width: max-content;
        flex-wrap: nowrap;
        background: #f8fafc;
    }

    /deep/ .w-e-bar-item {
        height: 42px;
        padding: 3px;
    }

    /deep/ .w-e-menu-tooltip-v5::before,
    /deep/ .w-e-menu-tooltip-v5::after {
        display: none !important;
        content: none !important;
    }
}

.mobile-question-add__image-input {
    position: fixed;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
}

.mobile-question-add__editor {
    min-height: 250px;
    background: #fff;

    /deep/ .w-e-text-container {
        min-height: 250px;
        font-size: 15px;
        line-height: 1.7;
    }

    /deep/ .w-e-text-placeholder {
        color: #a3acba;
        font-style: normal;
    }
}

.mobile-question-add__error {
    margin: 7px 2px 0;
    color: #e34d4a;
    font-size: 12px;
    line-height: 18px;
}

.mobile-question-add__tips {
    display: flex;
    margin-top: 12px;
    align-items: center;
    gap: 6px;
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    span {
        color: #929cac;
        font-size: 12px;
    }

    em {
        padding: 4px 8px;
        color: #52709a;
        font-size: 11px;
        font-style: normal;
        background: #edf4ff;
        border-radius: 10px;
    }
}

.mobile-question-add__footer {
    position: fixed;
    z-index: 90;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.96);
    border-top: 1px solid #e8ecf2;
    box-shadow: 0 -6px 20px rgba(31, 45, 61, 0.06);

    /deep/ .van-button {
        height: 46px;
        font-size: 15px;
        font-weight: 500;
        background: linear-gradient(90deg, #2478f2, #398fff);
        border: 0;
        box-shadow: 0 7px 16px rgba(36, 120, 242, 0.2);
    }
}
</style>
